import { Box, Button } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useFormContext } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import EmployeeSelector from 'modules/space_control/components/EmployeeSelector'
import type { WorkSpace as WorkSpaceNode } from 'modules/space_control/hooks/useWorkSpace'
import type { WorkSpaceSlotNode } from 'modules/space_control/hooks/useWorkSpaceSlots'
import { SlotSetup } from 'modules/space_control/lib/enums'

import BookingSlots from '../BookingSlots'
import SidepanelInfo from '../SidepanelInfo'

interface bookingFormFields {
  accessId: number
  date: string
  slots?: number[]
}

interface Props {
  onClose: () => void
  workSpace: WorkSpaceNode
  slots: WorkSpaceSlotNode[]
  isSlotsLoading: boolean
  edit?: boolean
  bookedSlots?: WorkSpaceSlotNode[]
  onChangeDate?: (date: string) => void
  accesses: User[]
}

const BookingSidepanel = ({
  onClose,
  workSpace,
  slots,
  isSlotsLoading,
  edit,
  bookedSlots = [],
  onChangeDate,
  accesses,
}: Props) => {
  const currentUser = users.current
  const bookingForm = useFormContext<bookingFormFields>()
  const isHalfDay = workSpace?.slotSetup === SlotSetup.half_day
  const handleSubmit = () => {
    bookingForm.handleSubmit()
    onClose()
  }

  const handleCancel = () => {
    track(trackingEvents.spaceControl.bookings.cancelBooking)
    onClose()
  }

  const onChangeTimeSlots = (value: number) => {
    const currentValues = bookingForm.values.slots
    if (currentValues && currentValues?.length > 0) {
      if (currentValues?.includes(value)) {
        const filteredValues = currentValues.filter((slot) => slot !== value)
        bookingForm.setFieldValue('slots', filteredValues)
      } else {
        bookingForm.setFieldValue('slots', [...currentValues, value])
      }
    } else {
      bookingForm.setFieldValue('slots', [value])
    }
  }
  if (workSpace)
    return (
      <Sidepanel
        onClose={onClose}
        closeLabel="close"
        title={
          edit
            ? i18n.t('space_control.overview.booking_sidepanel.edit_title')
            : i18n.t('space_control.overview.booking_sidepanel.title')
        }
        open
        footer={
          <>
            <Button onClick={handleCancel} type="secondary">
              {i18n.t('space_control.overview.booking_sidepanel.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                workSpace.slotSetup !== SlotSetup.whole_day &&
                bookingForm.values.slots?.length === 0
              }
            >
              {edit
                ? i18n.t('space_control.overview.booking_sidepanel.save_changes')
                : i18n.t('space_control.overview.booking_sidepanel.confirm')}
            </Button>
          </>
        }
      >
        <SidepanelInfo workSpace={workSpace} />
        <Box paddingTop="s32" gap="s24">
          <EmployeeSelector
            value={[bookingForm.values.accessId]}
            onChange={(accessId: number) => {
              bookingForm.setFieldValue('accessId', accessId)
            }}
            error={bookingForm.errors.accessId}
            accesses={accesses}
          />
          <DayPicker
            minDate={moment().startOf('day')}
            value={bookingForm.values.date}
            onChange={(value: string) => {
              const date = stringToDate(value, 'ui')
              if (onChangeDate) {
                onChangeDate(dateToString(date, 'backend'))
              }
              bookingForm.setFieldValue('date', date)
            }}
            error={bookingForm.errors.date}
            mandatory
            label={i18n.t('space_control.overview.booking_sidepanel.date_field_label')}
            firstDayOfWeek={firstDayOfWeekAsIndex(currentUser.attributes.first_day_of_week)}
          />
          {!isSlotsLoading && workSpace.slotSetup !== SlotSetup.whole_day && (
            <BookingSlots
              slots={slots}
              onChange={onChangeTimeSlots}
              selectedValues={bookingForm.values.slots ?? []}
              isHalfDay={isHalfDay}
            ></BookingSlots>
          )}
        </Box>
      </Sidepanel>
    )
  return <></>
}

export default observer(BookingSidepanel)
